import { ATOMIC_CHIP_CARET_MARKER } from '../contentEditableAtomicChipCaret';
import { MENTION_MARKDOWN_REGEX } from './mentionChipUtils';

export const INTERNAL_INVISIBLE_MARKER = ATOMIC_CHIP_CARET_MARKER;
export const CHIP_CARET_SPACER: string = INTERNAL_INVISIBLE_MARKER;
export const DISMISSED_MENTION_MARKER: string = INTERNAL_INVISIBLE_MARKER;
export const CHIP_CARET_SPACER_LENGTH: number = CHIP_CARET_SPACER.length;
export const DISMISSED_MENTION_MARKER_LENGTH: number = DISMISSED_MENTION_MARKER.length;

const INTERNAL_INVISIBLE_MARKER_REGEX = /\u200B/g;
const NON_BREAKING_SPACE_REGEX = /\u00a0/g;

export interface MentionTextSegment {
  type: 'text';
  text: string;
}

export interface MentionReferenceSegment {
  type: 'mention';
  name: string;
  deepLink: string;
}

export type MentionInputSegment = MentionTextSegment | MentionReferenceSegment;

export const stripInternalInvisibleMarkers = (text: string) => text.replace(INTERNAL_INVISIBLE_MARKER_REGEX, '');

export const normalizeMentionQuery = (text: string) => text.replace(NON_BREAKING_SPACE_REGEX, ' ');

export const hasMentionInputContent = ({ text, hasMentionChip }: { text: string; hasMentionChip: boolean }) =>
  stripInternalInvisibleMarkers(text).trim().length > 0 || hasMentionChip;

export const getTextLength = (text: string) => text.length;

export const getNextTextOffset = (text: string, offset: number) => {
  const codePoint = text.codePointAt(offset);
  if (codePoint === undefined) return offset;
  return Math.min(text.length, offset + (codePoint > 0xffff ? 2 : 1));
};

export const escapeMentionTriggers = (text: string) => text.replace(/@/g, `@${DISMISSED_MENTION_MARKER}`);

export const getDismissedMentionDeleteFrom = (text: string, offset: number) => {
  const markerIndex = offset - DISMISSED_MENTION_MARKER_LENGTH;
  if (markerIndex < 1 || text[markerIndex] !== DISMISSED_MENTION_MARKER || text[markerIndex - 1] !== '@') {
    return null;
  }
  return markerIndex - 1;
};

export const getDismissedMentionDeleteTo = (text: string, offset: number) => {
  const markerIndex = offset + 1;
  if (text[offset] !== '@' || text[markerIndex] !== DISMISSED_MENTION_MARKER) {
    return null;
  }
  return markerIndex + DISMISSED_MENTION_MARKER_LENGTH;
};

export const createDismissedMentionText = (content: string) =>
  content.startsWith('@') ? '@'.concat(DISMISSED_MENTION_MARKER, content.slice(1)) : content;

export const parseMentionMarkdown = (text: string): MentionInputSegment[] => {
  const segments: MentionInputSegment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  MENTION_MARKDOWN_REGEX.lastIndex = 0;
  while ((match = MENTION_MARKDOWN_REGEX.exec(text)) !== null) {
    const matchIndex: unknown = match.index;
    const matchText: unknown = match[0];
    const name: unknown = match[1];
    const deepLink: unknown = match[2];
    if (
      typeof matchIndex !== 'number' ||
      typeof matchText !== 'string' ||
      typeof name !== 'string' ||
      typeof deepLink !== 'string'
    ) {
      continue;
    }
    const before = text.slice(lastIndex, matchIndex);
    if (before) segments.push({ type: 'text', text: before });

    segments.push({ type: 'mention', name, deepLink });
    lastIndex = matchIndex + getTextLength(matchText);
  }

  const tail = text.slice(lastIndex);
  if (tail) segments.push({ type: 'text', text: tail });

  MENTION_MARKDOWN_REGEX.lastIndex = 0;
  return segments;
};

export const serializeMentionSegments = (segments: MentionInputSegment[]) =>
  segments
    .map((segment) =>
      segment.type === 'mention' ? '['.concat(segment.name, '](', segment.deepLink, ')') : segment.text,
    )
    .join('');
